import { VendorValue } from '@easy-expense/data-schema-v2';
import Data from '@easy-expense/frontend-data-layer';
import { getTranslation } from '@easy-expense/intl-client';
import { Icon } from '@easy-expense/ui-shared-components';
import { theme } from '@easy-expense/ui-theme';
import { Layout, OpenSans, Spacer } from '@easy-expense/ui-web-core';
import React from 'react';

import { Button } from '../Button.components';
import { LabelTextField } from '../LabelTextField.component';
import { SearchDropdown } from '../Shared/Dropdown/SearchDropDown.component';

export const UpsertVendor: React.FC<{
  onDone: () => void;
  onDiscard: () => void;
  selectedVendorKey?: string;
}> = ({ onDone, onDiscard, selectedVendorKey }) => {
  const vendor = Data.vendors.useByKey(selectedVendorKey);
  const categories = Data.expenseCategories.use();

  const [vendorValue, setVendorValue] = React.useState<VendorValue>({
    name: vendor?.value?.name ?? '',
    category: vendor?.value?.category,
    image: vendor?.value?.image,
  });

  const [nameErrorMessage, setNameErrorMessage] = React.useState<string>('');
  const cachedVendors = Data.vendors.use();
  const [nameInputFocused, setNameInputFocused] = React.useState<boolean>(false);

  React.useEffect(() => {
    setNameErrorMessage('');

    if (vendorValue.name === vendor?.value?.name) {
      return;
    }

    const doesSameNameVendorExist = cachedVendors.some((v) => v.value.name === vendorValue.name);

    if (doesSameNameVendorExist) {
      setNameErrorMessage(getTranslation('A vendor with the same name already exists'));
    }
  }, [vendorValue.name]);

  function onSave() {
    if (nameErrorMessage) {
      return;
    }

    try {
      if (vendor) {
        Data.vendors.update(vendor.key, { value: { ...vendorValue } });
      } else {
        Data.vendors.create({ value: vendorValue });
      }

      onDone();
    } catch (err: any) {
      if (!err?.issues) {
        alert(
          getTranslation('There was an error saving the vendor. Try again or contact support.'),
        );
        console.error(err);
        return;
      }

      const parsingError: any = err.issues.pop();
      if ((parsingError?.path as string[]).includes('name')) {
        setNameErrorMessage(getTranslation(`${parsingError.message}`));
      }
    }
  }

  return (
    <Layout.Column>
      <OpenSans.Primary size="xl-28" weight="bold-700">
        {getTranslation('Edit Vendor')}
      </OpenSans.Primary>

      <Spacer.Vertical size={16} />

      <Layout.Row>
        <LabelTextField
          label="Name"
          active={nameInputFocused}
          error={!!nameErrorMessage}
          errorMessage={nameErrorMessage}
        >
          <OpenSans.Input
            autoFocus
            name="vendor name"
            value={vendorValue?.name ?? ''}
            type="text"
            placeholder="Vendor Name"
            grow
            style={{ outline: 'none', width: 250 }}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const name = event.target.value;
              setVendorValue({ ...vendorValue, name });
            }}
            autoComplete="off"
            onFocus={() => setNameInputFocused(true)}
            onBlur={() => setNameInputFocused(false)}
          />
        </LabelTextField>

        <Spacer.Horizontal size={32} />
        <SearchDropdown
          header={getTranslation('Default Category')}
          placeholder={getTranslation('No Category')}
          selectedKey={vendorValue?.category ?? null}
          autoCompleteValues={categories}
          onSelect={(c) => {
            const category = c?.key || undefined;
            setVendorValue({ ...vendorValue, category });
          }}
          error={false}
          iconicIcon="pricetag-outline"
          clearOnSelect={true}
        />
      </Layout.Row>

      <Spacer.Vertical size={32} />

      <Layout.Row justify="flex-end">
        <Button.Secondary
          onClick={onDiscard}
          radius={50}
          style={{
            border: '2px solid #fff',
            boxShadow: '0 0 4px rgba(255, 255, 255, 1)',
          }}
        >
          <Icon
            size={15}
            color={theme.colors.brandPrimary}
            style={{ paddingRight: 10 }}
            name="chevron-back"
          />
          <OpenSans.Primary
            size={15}
            weight="bold-700"
            style={{ color: theme.colors.brandPrimary }}
          >
            {getTranslation('Discard')}
          </OpenSans.Primary>
        </Button.Secondary>
        <Spacer.Horizontal size={32} />
        <Button.Primary
          onClick={onSave}
          radius={50}
          style={{
            border: '2px solid #fff',
            boxShadow: '0 0 8px rgba(255, 255, 255, .5)',
            width: 'auto',
          }}
        >
          <OpenSans.Custom size={15} weight="bold-700" style={{ color: theme.colors.buttonWhite }}>
            {getTranslation('Save Vendor')}
          </OpenSans.Custom>
          <Icon
            size={15}
            color={theme.colors.buttonWhite}
            style={{ paddingLeft: 10 }}
            name="chevron-forward"
          />
        </Button.Primary>
      </Layout.Row>
    </Layout.Column>
  );
};
